"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatMenuItemPriceLabel } from "@/lib/menu-price-options";
import {
  GALLERY_ITEMS,
  displayMenuName,
  normalizeMenuName,
  type OwnerPopularGalleryItem,
} from "@/components/home/owner/owner-popular-gallery-items";
import type { Doc } from "../../../../convex/_generated/dataModel";

// The sheet chunk carries its own ConvexProvider (the homepage route skips
// the Convex client on purpose); load it only when a guest taps an add button.
const GalleryItemSheet = dynamic(
  () => import("@/components/home/owner/gallery-item-sheet"),
  { ssr: false },
);

export type { OwnerPopularGalleryItem } from "@/components/home/owner/owner-popular-gallery-items";

export interface OwnerPopularGalleryProps {
  /** Optional override. Defaults to the built-in 6 Tuscan food photos. */
  items?: OwnerPopularGalleryItem[];
  /**
   * Server-fetched menu rows for the gallery dishes. When present, cards
   * become shoppable (name, price, and an add button that opens the real
   * item customization sheet). Omitted in browse-only mode, where the
   * gallery stays a photo lightbox.
   */
  menuItems?: Doc<"menu_items">[];
  className?: string;
}

export function OwnerPopularGallery({
  items = GALLERY_ITEMS,
  menuItems,
  className,
}: OwnerPopularGalleryProps): React.JSX.Element {
  const count = items.length;
  const [index, setIndex] = React.useState<number | null>(null);
  const [customizingName, setCustomizingName] = React.useState<string | null>(
    null,
  );

  const menuByName = React.useMemo(() => {
    const map = new Map<string, Doc<"menu_items">>();
    for (const menuItem of menuItems ?? []) {
      map.set(normalizeMenuName(menuItem.name), menuItem);
    }
    return map;
  }, [menuItems]);

  const customizingItem = customizingName
    ? (menuByName.get(normalizeMenuName(customizingName)) ?? null)
    : null;

  const open = React.useCallback((i: number) => setIndex(i), []);
  const close = React.useCallback(() => setIndex(null), []);
  const prev = React.useCallback(
    () => setIndex((i) => (i === null ? i : (i - 1 + count) % count)),
    [count],
  );
  const next = React.useCallback(
    () => setIndex((i) => (i === null ? i : (i + 1) % count)),
    [count],
  );

  const isOpen = index !== null;

  // ArrowLeft / ArrowRight while the lightbox is open. Escape and Tab are
  // handled by Radix. Listener is gated on the open state and cleaned up.
  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        prev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        next();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, prev, next]);

  const active = index !== null ? items[index] : null;
  const position = index !== null ? index + 1 : 0;

  return (
    <section
      data-component="owner-gallery"
      aria-labelledby="owner-gallery-title"
      className={cn(
        "mx-auto max-w-7xl px-4 py-14 font-system md:px-6 md:py-20",
        className,
      )}
    >
      <div className="mx-auto flex flex-col gap-6">
        <div className="flex flex-col gap-2 md:max-w-2xl">
          <h2
            id="owner-gallery-title"
            className="m-0 text-[clamp(1.625rem,4vw,2rem)] font-bold leading-tight tracking-tight text-[#1a1a1a]"
          >
            See our most popular items
          </h2>
          <p className="m-0 text-[15px] leading-relaxed text-neutral-500 sm:text-base">
            Here&rsquo;s a sneak peek of the dishes our guests can&rsquo;t stop
            talking about.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-x-2 gap-y-5 md:grid-cols-3 md:gap-x-4 md:gap-y-7">
          {items.map((item, i) => {
            const menuItem = menuByName.get(normalizeMenuName(item.menuName));
            const shoppable = menuItem !== undefined;
            const name = displayMenuName(item.menuName);

            return (
              <div key={`${item.src}-${i}`} className="flex flex-col gap-2">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => open(i)}
                    aria-label={`View: ${item.alt}`}
                    className="group relative flex aspect-square w-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f45c24] focus-visible:ring-offset-2"
                  >
                    <div className="absolute inset-0 overflow-hidden rounded-xl bg-neutral-100 transition-transform duration-300 group-hover:scale-[1.01] motion-safe:transition-transform md:group-hover:scale-[1.02]">
                      <Image
                        src={item.src}
                        alt={item.alt}
                        fill
                        sizes="(max-width:768px) 50vw, 33vw"
                        className="object-cover"
                      />
                    </div>
                  </button>
                  {shoppable ? (
                    <button
                      type="button"
                      onClick={() => setCustomizingName(item.menuName)}
                      aria-label={`Customize ${name}`}
                      data-gallery-add
                      className="absolute right-2 top-2 flex size-10 items-center justify-center rounded-full bg-white text-[#0d0d0d] shadow-[0_2px_10px_rgb(0_0_0_/_0.18)] transition-transform duration-150 hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f45c24] focus-visible:ring-offset-2"
                    >
                      <Plus className="h-5 w-5" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
                {shoppable ? (
                  <div className="flex items-baseline justify-between gap-2 px-0.5">
                    <p className="m-0 truncate text-[15px] font-semibold text-[#1a1a1a]">
                      {name}
                    </p>
                    <p className="m-0 shrink-0 text-[14px] font-medium text-neutral-500">
                      {formatMenuItemPriceLabel(menuItem)}
                    </p>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {customizingItem ? (
        <GalleryItemSheet
          item={customizingItem}
          onClose={() => setCustomizingName(null)}
        />
      ) : null}

      <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
        <DialogContent
          showCloseButton={false}
          overlayClassName="bg-black/90"
          className="mx-auto w-full max-w-[min(100vw,90vh)] border-0 bg-transparent p-0 shadow-none"
        >
          <DialogTitle className="sr-only">Image gallery</DialogTitle>
          <DialogDescription className="sr-only" aria-live="polite">
            {active ? `Image ${position} of ${count}: ${active.alt}` : ""}
          </DialogDescription>

          <div className="relative mx-auto aspect-square w-full max-w-[min(100vw,90vh)]">
            {active ? (
              <Image
                src={active.src}
                alt={active.alt}
                fill
                sizes="90vw"
                className="object-contain"
              />
            ) : null}

            <DialogClose
              aria-label="Close gallery"
              className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#1a1a1a] shadow hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f45c24] focus-visible:ring-offset-2"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </DialogClose>

            <button
              type="button"
              aria-label="Previous image"
              onClick={prev}
              className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#1a1a1a] shadow hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f45c24] focus-visible:ring-offset-2"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>

            <button
              type="button"
              aria-label="Next image"
              onClick={next}
              className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#1a1a1a] shadow hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f45c24] focus-visible:ring-offset-2"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default OwnerPopularGallery;
